/** Session cache of hole rows keyed by DataSet + Hole_ID. Overlapping filters reuse hits. */

import { cacheHoleRecords, readAllCachedHoleRecords } from './holeOfflineCache';

export function holeRecordKey(dataset, holeId) {
  return `${String(dataset ?? '')}\0${String(holeId ?? '')}`;
}

export function holeDisplayKey(dataset, holeId) {
  return `${String(dataset ?? '')}::${String(holeId ?? '')}`;
}

export function keyFromRow(row) {
  if (!row) return '';
  return holeRecordKey(row.DataSet, row.Hole_ID ?? row.holeID);
}

export function displayKeyFromRow(row) {
  if (!row) return '';
  return holeDisplayKey(row.DataSet, row.Hole_ID ?? row.holeID);
}

function emptyRecord() {
  return {
    collar: null,
    surveys: [],
    assays: [],
    geology: [],
    hasCollar: false,
    hasSurveys: false,
    hasAssays: false,
    hasGeology: false,
    missing: false,
  };
}

const records = new Map();
const listeners = new Set();
const dirtyKeys = new Set();
let persistTimer = null;
let hydratePromise = null;

function recordCompleteness(rec) {
  if (!rec) return 0;
  return (rec.hasCollar ? 1 : 0) + (rec.hasSurveys ? 1 : 0) + (rec.hasAssays ? 1 : 0) + (rec.hasGeology ? 1 : 0);
}

function serializeRecord(id, rec) {
  return {
    id,
    collar: rec.collar,
    surveys: rec.surveys,
    assays: rec.assays,
    geology: rec.geology,
    hasCollar: rec.hasCollar,
    hasSurveys: rec.hasSurveys,
    hasAssays: rec.hasAssays,
    hasGeology: rec.hasGeology,
    missing: rec.missing,
    updatedAt: Date.now(),
  };
}

function markDirty(key) {
  if (!key || key === '\0') return;
  dirtyKeys.add(key);
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    const ids = Array.from(dirtyKeys);
    dirtyKeys.clear();
    const rows = ids
      .map((id) => {
        const rec = records.get(id);
        if (!rec?.hasCollar) return null;
        return serializeRecord(id, rec);
      })
      .filter(Boolean);
    if (rows.length) cacheHoleRecords(rows);
  }, 500);
}

function notifyHoleRecords() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // ignore subscriber errors
    }
  });
}

export function subscribeHoleRecords(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function ensure(key) {
  let rec = records.get(key);
  if (!rec) {
    rec = emptyRecord();
    records.set(key, rec);
  }
  return rec;
}

export function getHoleRecord(dataset, holeId) {
  return records.get(holeRecordKey(dataset, holeId)) || null;
}

export function getHoleRecordByKey(key) {
  return records.get(key) || null;
}

export function listCollarRecords() {
  const out = [];
  records.forEach((rec, key) => {
    if (!rec?.hasCollar || !rec.collar) return;
    const idx = key.indexOf('\0');
    out.push({
      key,
      dataset: idx >= 0 ? key.slice(0, idx) : '',
      holeId: idx >= 0 ? key.slice(idx + 1) : key,
      rec,
    });
  });
  return out;
}

export function putCollars(rows = []) {
  (rows || []).forEach((collar) => {
    const key = keyFromRow(collar);
    if (!key || key === '\0') return;
    const rec = ensure(key);
    rec.collar = collar;
    rec.hasCollar = true;
    rec.missing = false;
    markDirty(key);
  });
  if (rows?.length) notifyHoleRecords();
}

export function markCollarMisses(keys = []) {
  (keys || []).forEach((row) => {
    const key = keyFromRow(row);
    if (!key || key === '\0') return;
    const rec = ensure(key);
    if (!rec.hasCollar) rec.missing = true;
  });
}

export function putSurveys(requestedKeys = [], rows = []) {
  const grouped = new Map();
  (rows || []).forEach((row) => {
    const key = keyFromRow(row);
    if (!key || key === '\0') return;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(row);
  });
  const mark = requestedKeys.length ? requestedKeys : Array.from(grouped.keys()).map((key) => {
    const [DataSet, Hole_ID] = key.split('\0');
    return { DataSet, Hole_ID };
  });
  mark.forEach((row) => {
    const key = typeof row === 'string' ? row : keyFromRow(row);
    if (!key || key === '\0') return;
    const rec = ensure(key);
    rec.surveys = grouped.get(key) || [];
    rec.hasSurveys = true;
    markDirty(key);
  });
  notifyHoleRecords();
}

export function putAssays(requestedKeys = [], rows = []) {
  const grouped = new Map();
  (rows || []).forEach((row) => {
    const key = keyFromRow(row);
    if (!key || key === '\0') return;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(row);
  });
  (requestedKeys || []).forEach((row) => {
    const key = keyFromRow(row);
    if (!key || key === '\0') return;
    const rec = ensure(key);
    rec.assays = grouped.get(key) || [];
    rec.hasAssays = true;
    markDirty(key);
  });
  notifyHoleRecords();
}

export function putGeology(requestedKeys = [], rows = []) {
  const grouped = new Map();
  (rows || []).forEach((row) => {
    const key = keyFromRow(row);
    if (!key || key === '\0') return;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(row);
  });
  (requestedKeys || []).forEach((row) => {
    const key = keyFromRow(row);
    if (!key || key === '\0') return;
    const rec = ensure(key);
    rec.geology = grouped.get(key) || [];
    rec.hasGeology = true;
    markDirty(key);
  });
  notifyHoleRecords();
}

export function missingKeys(keys = [], field) {
  return (keys || []).filter((row) => {
    const rec = records.get(keyFromRow(row));
    if (!rec) return true;
    if (field === 'collar') return !rec.hasCollar && !rec.missing;
    if (field === 'surveys') return !rec.hasSurveys;
    if (field === 'assays') return !rec.hasAssays;
    if (field === 'geology') return !rec.hasGeology;
    return true;
  });
}

export function clearHoleRecordStore() {
  records.clear();
  dirtyKeys.clear();
  notifyHoleRecords();
}

export async function hydrateHoleRecordStore() {
  if (hydratePromise) return hydratePromise;
  hydratePromise = (async () => {
    const rows = await readAllCachedHoleRecords();
    rows.forEach((row) => {
      const id = row?.id;
      if (!id) return;
      const incoming = {
        collar: row.collar || null,
        surveys: row.surveys || [],
        assays: row.assays || [],
        geology: row.geology || [],
        hasCollar: !!row.hasCollar,
        hasSurveys: !!row.hasSurveys,
        hasAssays: !!row.hasAssays,
        hasGeology: !!row.hasGeology,
        missing: !!row.missing,
      };
      const existing = records.get(id);
      if (existing && recordCompleteness(existing) >= recordCompleteness(incoming)) return;
      records.set(id, incoming);
    });
    if (rows.length) notifyHoleRecords();
  })();
  return hydratePromise;
}
